import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Search,
  Loader2,
  CheckCircle2,
  X,
  Tag,
  Clock,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

interface RejectionTemplate {
  id: string;
  code: string;
  name: string;
  category: string;
  message: string;
  internalNotes?: string;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

const CATEGORIES = [
  { value: 'DOCUMENT_QUALITY', label: 'Qualidade do Documento' },
  { value: 'DOCUMENT_EXPIRED', label: 'Documento Expirado' },
  { value: 'DOCUMENT_INCOMPLETE', label: 'Documento Incompleto' },
  { value: 'DATA_MISMATCH', label: 'Dados Divergentes' },
  { value: 'FRAUD_SUSPICION', label: 'Suspeita de Fraude' },
  { value: 'OTHER', label: 'Outros' },
];

export function KycTemplates() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RejectionTemplate | null>(null);
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery<RejectionTemplate[]>({
    queryKey: ['kyc-rejection-templates', selectedCategory],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      const response = await api.get(`/admin/kyc/templates?${params}`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/kyc/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.patch(`/admin/kyc/templates/${id}`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
    },
  });

  const filteredTemplates = templates?.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (template: RejectionTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleCopy = (template: RejectionTemplate) => {
    navigator.clipboard.writeText(template.message);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
            <FileText className="w-7 h-7 text-primary-400" />
            Templates de Rejeição
          </h1>
          <p className="text-surface-400 mt-1">
            Gerencie templates de mensagens para rejeição de KYC
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar template..."
            className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
        >
          <option value="">Todas as categorias</option>
          {CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates?.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => handleEdit(template)}
              onDelete={() => deleteMutation.mutate(template.id)}
              onCopy={() => handleCopy(template)}
              onToggle={() => toggleMutation.mutate({ id: template.id, isActive: !template.isActive })}
            />
          ))}
        </div>
      )}

      {filteredTemplates?.length === 0 && !isLoading && (
        <div className="text-center py-12 text-surface-500">
          Nenhum template encontrado
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
          }}
        />
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onEdit,
  onDelete,
  onCopy,
  onToggle,
}: {
  template: RejectionTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onToggle: () => void;
}) {
  const categoryLabel = CATEGORIES.find(c => c.value === template.category)?.label || template.category;

  return (
    <div className={cn(
      "glass rounded-xl p-4 transition-all",
      !template.isActive && "opacity-60"
    )}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded">
              {template.code}
            </span>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded",
              template.isActive
                ? "bg-green-500/20 text-green-400"
                : "bg-surface-700 text-surface-400"
            )}>
              {template.isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
          <h3 className="font-medium text-surface-100">{template.name}</h3>
        </div>
        <button
          onClick={onToggle}
          className="text-surface-400 hover:text-surface-100 transition-colors"
        >
          {template.isActive ? (
            <ToggleRight className="w-6 h-6 text-green-400" />
          ) : (
            <ToggleLeft className="w-6 h-6" />
          )}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-3 h-3 text-surface-500" />
        <span className="text-xs text-surface-500">{categoryLabel}</span>
      </div>

      <p className="text-sm text-surface-400 mb-4 line-clamp-3">
        {template.message}
      </p>

      <div className="flex items-center justify-between pt-3 border-t border-surface-700">
        <span className="text-xs text-surface-500">
          Usado {template.usageCount}x
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onCopy}
            className="p-1.5 text-surface-400 hover:text-surface-100 hover:bg-surface-700 rounded transition-colors"
            title="Copiar mensagem"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-surface-400 hover:text-primary-400 hover:bg-surface-700 rounded transition-colors"
            title="Editar"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-surface-400 hover:text-red-400 hover:bg-surface-700 rounded transition-colors"
            title="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateModal({
  template,
  onClose,
  onSuccess,
}: {
  template: RejectionTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    code: template?.code || '',
    name: template?.name || '',
    category: template?.category || 'OTHER',
    message: template?.message || '',
    internalNotes: template?.internalNotes || '',
    isActive: template?.isActive ?? true,
  });

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (template) {
        const response = await api.put(`/admin/kyc/templates/${template.id}`, data);
        return response.data;
      } else {
        const response = await api.post('/admin/kyc/templates', data);
        return response.data;
      }
    },
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-900 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-surface-700">
          <h2 className="text-lg font-semibold text-surface-100">
            {template ? 'Editar Template' : 'Novo Template'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-surface-400 hover:text-surface-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Código
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
                placeholder="DOC_QUALITY_01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Categoria
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Nome
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
              placeholder="Documento ilegível"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Mensagem para o Cliente
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500 min-h-[100px]"
              placeholder="O documento enviado está ilegível. Por favor, envie uma nova foto com melhor qualidade..."
              required
            />
            <p className="text-xs text-surface-500 mt-1">
              Esta mensagem será exibida ao cliente quando o KYC for rejeitado
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Notas Internas (opcional)
            </label>
            <textarea
              value={formData.internalNotes}
              onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500 min-h-[60px]"
              placeholder="Notas visíveis apenas para analistas..."
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
            <span className="text-sm text-surface-300">Template Ativo</span>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
              className={cn(
                "w-12 h-6 rounded-full transition-colors relative",
                formData.isActive ? "bg-green-500" : "bg-surface-600"
              )}
            >
              <div className={cn(
                "w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform",
                formData.isActive ? "translate-x-6" : "translate-x-0.5"
              )} />
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {template ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default KycTemplates;
